import React from "react";
import { formatCurrency, formatDate } from "../shared/format";
import { CompactIcon } from "../shared/CompactUI";

export type PromoUsageSummary = {
    usageCount: number;
    last30Days?: number;
    last7Days?: number;
    lastUsedAt?: string | null;
    paidInRub?: number;
    paid?: number;
};
export type PromoAnalyticsResponse = {
    overall?: PromoUsageSummary;
    period?: { from: string; to: string; timezone: string };
    daily?: Array<{ date: string; usageCount: number }>;
};
const countFormatter = new Intl.NumberFormat("ru-RU");
export function usageCount(value: unknown): string {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? countFormatter.format(value) : "—";
}

export const PromoUsage: React.FC<{ title: string; data: PromoAnalyticsResponse | null; loading: boolean; error: string | null; onRetry: () => void }> = ({ title, data, loading, error, onRetry }) => {
    const summary = data?.overall;
    const daily = data?.daily ?? [];
    const max = Math.max(1, ...daily.map(day => day.usageCount));
    return <section className="section-card promo-analytics" aria-label={`Аналитика промокода ${title}`} aria-busy={loading}>
        <div className="section-item-head">
            <h2 className="section-subtitle">Применения · {title}</h2>
            <button className="section-btn-secondary promo-analytics-refresh" type="button" disabled={loading} onClick={onRetry} aria-label="Обновить аналитику промокода"><CompactIcon name="refresh" /></button>
        </div>
        <p className="section-helper">Только выбранный салон</p>
        {loading ? <p className="section-helper" role="status">Загрузка аналитики…</p> : error ? <p className="section-error" role="alert">{error}</p> : summary ? <>
            <div className="promo-usage-metrics">
                <div><span>За всё время</span><strong>{usageCount(summary.usageCount)}</strong></div>
                <div><span>За 30 дней</span><strong>{usageCount(summary.last30Days)}</strong></div>
                <div><span>За 7 дней</span><strong>{usageCount(summary.last7Days)}</strong></div>
            </div>
            <p className="section-helper promo-last-used">{summary.usageCount === 0 ? "Применений пока нет" : `Последнее применение: ${summary.lastUsedAt ? formatDate(summary.lastUsedAt, true) : "дата не указана"}`}</p>
            {typeof summary.paidInRub === "number" ? <p className="section-helper">Оплачено рублями: {formatCurrency(summary.paidInRub)}{typeof summary.paid === "number" ? ` · Всего: ${formatCurrency(summary.paid)}` : ""}</p> : null}
            {daily.length > 0 && data?.period ? <>
                <div className="promo-chart-title">За последние 30 дней <span>{usageCount(summary.last30Days)}</span></div>
                <div className="promo-usage-chart" role="img" aria-label={`Применений за 30 дней: ${usageCount(summary.last30Days)}. По дням — в таблице ниже.`}>
                    {daily.map(day => <div key={day.date} title={`${formatDate(day.date)}: ${usageCount(day.usageCount)}`}><span style={{ height: `${day.usageCount / max * 100}%` }} /></div>)}
                </div>
                <div className="promo-chart-axis"><span>{formatDate(data.period.from)}</span><span>{formatDate(data.period.to)}</span></div>
                <details className="promo-usage-days"><summary>Применения по дням<CompactIcon name="chevron" /></summary>
                    <table><caption>Даты по времени салона · {data.period.timezone}</caption><thead><tr><th scope="col">Дата</th><th scope="col">Применений</th></tr></thead>
                        <tbody>{daily.map(day => <tr key={day.date}><th scope="row">{formatDate(day.date)}</th><td>{usageCount(day.usageCount)}</td></tr>)}</tbody>
                    </table>
                </details>
            </> : null}
            <details className="promo-usage-days"><summary>Как считаем применения<CompactIcon name="chevron" /></summary>
                <p className="section-helper promo-count-note">Одна запись с несколькими услугами — одно применение. Операции без записи считаются отдельно. Удалённые операции не учитываются.</p>
            </details>
        </> : <p className="section-helper">Аналитика пока недоступна. Попробуйте обновить.</p>}
    </section>;
};
